import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago'});
  } catch {
    return '';
  }
};

/**
 * Recently published Relo News feed - inline on the portal dashboard.
 *
 * Pulls the latest 3 published articles from /api/news (public, no auth)
 * so even free-trial users can browse without bouncing to the public site.
 */
const RecentlyPublishedFeed = ({ limit = 3 }) => {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/news?limit=${limit}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setItems(json.articles || []);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  if (err) return null;

  return (
    <section
      data-testid="dashboard-recently-published"
      className="bg-white border border-[#EFE6CF] rounded-xl p-6 md:p-7"
    >
      <header className="flex items-end justify-between mb-5">
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1">
            Relo News
          </p>
          <h3 className="font-display italic text-2xl text-[#0A1F2E] leading-tight">
            Recently published
          </h3>
        </div>
        <Link
          to="/relo-news"
          data-testid="dashboard-recently-published-all"
          className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] hover:text-[#2CA8A0] flex items-center gap-1"
        >
          See all <ArrowRight className="w-3 h-3" />
        </Link>
      </header>

      {!items ? (
        <ul className="space-y-4">
          {[0, 1, 2].slice(0, limit).map((i) => (
            <li key={i} className="h-20 bg-[#F4ECD8]/40 rounded animate-pulse" />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <p className="font-body text-sm text-[#0A1F2E]/55 italic">
          No articles yet - check back soon.
        </p>
      ) : (
        <ul className="space-y-4" data-testid="dashboard-recently-published-list">
          {items.slice(0, limit).map((a) => (
            <li key={a.slug} data-testid={`dashboard-rp-${a.slug}`}>
              <Link
                to={`/relo-news/${a.slug}`}
                className="block group"
              >
                <div className="flex items-start gap-2 mb-1.5">
                  <span className="font-mono uppercase text-[9px] tracking-[0.15em] bg-[#E8A830] text-[#0A1F2E] px-2 py-0.5 rounded-full font-semibold">
                    {a.category_label}
                  </span>
                  <span className="font-mono uppercase text-[10px] tracking-[0.12em] text-[#0A1F2E]/50 flex items-center gap-1">
                    {formatDate(a.published_at)}
                    {a.read_time && (
                      <>
                        <span className="opacity-40">·</span>
                        <Clock className="w-3 h-3" /> {a.read_time}
                      </>
                    )}
                  </span>
                </div>
                <h4 className="font-display italic text-lg text-[#0A1F2E] leading-snug group-hover:text-[#2CA8A0] transition">
                  {a.title}
                </h4>
                <p className="font-body text-sm text-[#0A1F2E]/65 leading-relaxed mt-1 line-clamp-2">
                  {a.excerpt}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default RecentlyPublishedFeed;
